/**
 * Billing page — счётчик диалогов, подписка с брэкетами сверхлимита, оплата 50/50.
 * GET /api/billing?client_id=<activeClientId>
 *
 * КАНОН: имена полей = backend BillingResponse (billing_router.py, прайс v3.1):
 *   client_id, client_name, period_start/end, dialogs_total/included/billable,
 *   subscription_base, brackets[{label,from,to,rate,count,amount}],
 *   overage_total, invoice_total, payment_plan, paid_amount, contract_total.
 *
 * Статус 50/50 бэк отдельным объектом НЕ отдаёт — выводим на фронте:
 * транши = 50% от contract_total; first_paid = paid_amount >= 1-й транш;
 * second_paid = paid_amount >= contract_total; contract_total = 0 → договора нет.
 */

import { CreditCard, MessageCircle, CheckCircle2, Clock } from 'lucide-react'
import { useBilling } from '@/hooks/useBilling'
import { useActiveClient } from '@/hooks/useClients'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatRub } from '@/lib/utils'

const PERIOD_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })

function formatPeriod(startIso: string, endIso: string): string {
  try {
    return `${PERIOD_FMT.format(new Date(startIso))} — ${PERIOD_FMT.format(new Date(endIso))}`
  } catch {
    return ''
  }
}

export function BillingPage() {
  const { activeClientId } = useActiveClient()
  const { data: billing, isLoading, error } = useBilling(activeClientId)

  if (!activeClientId) {
    return (
      <div className="p-4 text-muted-foreground">
        Выберите клиента в верхней панели, чтобы увидеть биллинг.
      </div>
    )
  }

  if (isLoading) return <div className="p-4 text-muted-foreground">Загрузка биллинга...</div>
  if (error || !billing) return <div className="p-4 text-destructive">Ошибка загрузки биллинга</div>

  const dialogs = billing.dialogs_total
  const included = billing.dialogs_included
  const overPerc = included > 0 ? Math.min(Math.round((dialogs / included) * 100), 200) : 0
  const barWidth = Math.min(overPerc, 100)
  const isOver = billing.dialogs_billable > 0
  const brackets = billing.brackets ?? []

  // ---- Договор 50/50 — выводим из contract_total / paid_amount ----
  const contractTotal = billing.contract_total
  const hasContract = contractTotal > 0
  const firstTranche = Math.round(contractTotal * 0.5)
  const secondTranche = contractTotal - firstTranche
  const paidAmount = billing.paid_amount
  const firstPaid = hasContract && paidAmount >= firstTranche
  const secondPaid = hasContract && paidAmount >= contractTotal

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Биллинг</h1>
        <p className="text-sm text-muted-foreground">
          Клиент: {billing.client_name || billing.client_id} · период{' '}
          {formatPeriod(billing.period_start, billing.period_end)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dialog counter */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Диалоги</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground">
              1 диалог = уникальный клиент/сутки
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-3">
              <div>
                <p className="text-3xl font-bold">{dialogs}</p>
                <p className="text-xs text-muted-foreground">из {included} включено</p>
              </div>
              {isOver && (
                <Badge variant="urgency_yellow" className="mb-1">
                  +{billing.dialogs_billable} сверх лимита
                </Badge>
              )}
            </div>

            {/* Progress bar */}
            <div className="space-y-1">
              <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isOver ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0</span>
                <span>{included} (включено)</span>
              </div>
            </div>

            {/* Subscription + overage brackets */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Подписка ({included} диалогов)</span>
                <span className="font-medium">{formatRub(billing.subscription_base)}/мес</span>
              </div>
              {brackets.map((br) => (
                <div key={br.label} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {br.label} · {br.count} × {formatRub(br.rate)}
                  </span>
                  <span className="font-medium">{formatRub(br.amount)}</span>
                </div>
              ))}
              {billing.overage_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Сверхлимит итого</span>
                  <span className="font-medium">{formatRub(billing.overage_total)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Итого к оплате</span>
                <span>{formatRub(billing.invoice_total)}</span>
              </div>
            </div>

            {/* Brackets reference */}
            <div className="text-xs text-muted-foreground space-y-0.5 border-t pt-2">
              <p className="font-medium text-foreground mb-1">Брэкеты сверхлимита:</p>
              <p>101–300 диалогов: 15 ₽/шт</p>
              <p>301–1 000 диалогов: 10 ₽/шт</p>
              <p>1 001–2 000 диалогов: 7 ₽/шт</p>
              <p>2 000+ диалогов: 5 ₽/шт</p>
            </div>
          </CardContent>
        </Card>

        {/* Payment 50/50 status — derived from contract_total / paid_amount */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Оплата по договору 50/50</CardTitle>
            </div>
            {hasContract && (
              <p className="text-xs text-muted-foreground">
                Сумма договора: {formatRub(contractTotal)} (базовый пакет + доп. каналы)
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {!hasContract ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Договор ещё не оформлен
              </p>
            ) : (
              <>
                {/* First tranche */}
                <div className={`flex items-center justify-between rounded-md border p-3 ${firstPaid ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
                  <div>
                    <p className="text-sm font-medium">Первые 50% — предоплата</p>
                    <p className="text-xs text-muted-foreground">{formatRub(firstTranche)}</p>
                  </div>
                  {firstPaid ? (
                    <div className="flex items-center gap-1.5 text-green-700 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      Получены
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                      <Clock className="h-4 w-4" />
                      Ожидается
                    </div>
                  )}
                </div>

                {/* Second tranche */}
                <div className={`flex items-center justify-between rounded-md border p-3 ${secondPaid ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
                  <div>
                    <p className="text-sm font-medium">Вторые 50% — после hypercare</p>
                    <p className="text-xs text-muted-foreground">{formatRub(secondTranche)}</p>
                  </div>
                  {secondPaid ? (
                    <div className="flex items-center gap-1.5 text-green-700 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      Получены
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                      <Clock className="h-4 w-4" />
                      Не выставлен
                    </div>
                  )}
                </div>

                {/* Paid summary */}
                <div className="rounded-md bg-muted p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Оплачено</span>
                    <span className="font-medium">{formatRub(paidAmount)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-muted-foreground">Остаток</span>
                    <span className="font-medium">{formatRub(Math.max(0, contractTotal - paidAmount))}</span>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Подписка {formatRub(billing.subscription_base)}/мес выставляется отдельно с момента запуска.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
